import { lazyZodSchema } from './lazy-zod';

export const createNormalizedIsoDate = (z: any) =>
  z.preprocess((val: any) => {
    if (val === null || val === undefined) return val;
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (trimmed === "") return null;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(trimmed)) {
        return trimmed;
      }
      const parsed = Date.parse(trimmed);
      if (!isNaN(parsed)) {
        return new Date(parsed).toISOString();
      }
      return val;
    }
    if (val instanceof Date) {
      return isNaN(val.getTime()) ? val : val.toISOString();
    }
    if (typeof val.toDate === "function") {
      const d = val.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d.toISOString() : val;
    }
    if (typeof val === "object") {
      const sec = typeof val._seconds === "number" ? val._seconds : typeof val.seconds === "number" ? val.seconds : null;
      if (sec !== null) {
        const nano = typeof val._nanoseconds === "number" ? val._nanoseconds : typeof val.nanoseconds === "number" ? val.nanoseconds : typeof val.nanos === "number" ? val.nanos : 0;
        return new Date(sec * 1000 + Math.floor(nano / 1e6)).toISOString();
      }
    }
    if (typeof val === "number" && !isNaN(val) && isFinite(val)) {
      const ms = val > 1e11 ? val : val * 1000;
      const d = new Date(ms);
      return !isNaN(d.getTime()) ? d.toISOString() : val;
    }
    return val;
  }, z.string().optional().nullable());

export const NormalizedIsoDate = lazyZodSchema(createNormalizedIsoDate);

export const VehicleSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  description: z.string().optional().nullable(),
  fleetGroupId: z.string().optional().nullable(),
  fuelTypeId: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  measurementUnit: z.string().optional().nullable(),
  fuelCapacity: z.union([z.number(), z.string()]).optional().nullable(),
  expectedConsumptionRate: z.union([z.number(), z.string()]).optional().nullable(),
  name: z.string(),
  registrationNumber: z.string().optional().nullable(),
  useForRebateClaim: z.boolean().default(true),
  lastOdometer: z.union([z.number(), z.string()]).optional().nullable(),
  lastOdometerDate: createNormalizedIsoDate(z),
  metadata: z.any().optional(),
}));

export const VehicleGroupSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  description: z.string().optional().nullable().default(""),
  name: z.string(),
  metadata: z.any().optional(),
}));

export const FleetOperatorSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  employeeCode: z.string().optional().nullable(),
  fullName: z.string(),
  identification: z.string().optional().nullable(),
  nickName: z.string().optional().nullable(),
  metadata: z.any().optional(),
}));

export const ActivitySchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  rebateEligible: z.boolean().default(true),
  name: z.string(),
  metadata: z.any().optional(),
}));

export const SupplierSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  isActive: z.boolean(),
  name: z.string(),
  metadata: z.any().optional(),
}));

export const SectionSchema = lazyZodSchema((z) => z.object({
  areaSizeHectares: z.union([z.number(), z.string()]).default(0),
  id: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  sectionName: z.string(),
  metadata: z.any().optional(),
}));

export const ZoneSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  zoneName: z.string(),
  isActive: z.boolean(),
  sections: z.array(SectionSchema).optional(),
  metadata: z.any().optional(),
}));

export const TankSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  fuelReceiveBulkDelivery: z.boolean().default(true),
  fuelType: z.string().optional().nullable(),
  fuelTypeId: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  mobileTank: z.boolean().default(false),
  name: z.string(),
  size: z.union([z.number(), z.string()]).optional().nullable(),
  lastDipReading: z.union([z.number(), z.string()]).optional().nullable(),
  lastDipReadingDate: createNormalizedIsoDate(z),
  metadata: z.any().optional(),
}));

export const ProductSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  isActive: z.boolean(),
  name: z.string(),
  metadata: z.any().optional(),
}));

export const FuelTypeSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  companyId: z.string(),
  name: z.string(),
  isActive: z.boolean().default(true),
  metadata: z.any().optional(),
}));

export const CompanySchema = lazyZodSchema((z) => z.object({
  id: z.string().optional(),
  companyId: z.string().optional(),
  name: z.string().min(1, "Company name is required"),
  owner: z.string().min(1, "Owner is required"),
  street: z.string().optional().nullable(),
  province: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  activationCode: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  addresLine1: z.string().optional().nullable(),
  addresLine2: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  logoUrl: z.string().optional().nullable(),
  fuelUnitOfMeasure: z.enum(["LITRES", "GALLONS"]).optional().nullable(),
  selectedTemplates: z.record(z.string(), z.any()).optional().nullable(),
  whatsappConfig: z.record(z.string(), z.any()).optional().nullable(),
  inAppValidationConfig: z.record(z.string(), z.any()).optional().nullable(),
  metadata: z.any().optional(),
}));

export const UserSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional(),
  uid: z.string().min(1, "UID is required"),
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  email: z.string().email().optional().nullable(),
  emailVerified: z.boolean().optional(),
  isActive: z.boolean().default(true),
  admin: z.boolean().optional(),
  companyId: z.string().optional().nullable(),
  fleetOperatorId: z.string().optional().nullable(),
  displayName: z.string().optional().nullable(),
  nextOnboardingStep: z.string().optional().nullable(),
  selectedTemplates: z.record(z.string(), z.any()).optional().nullable(),
  metadata: z.any().optional(),
}));

export const BulkUserCreateSchema = lazyZodSchema((z) => z.object({
  companyId: z.string().min(1, "Company ID is required"),
  users: z.array(z.object({
    email: z.string().email("Valid email required"),
    firstName: z.string().min(1, "First name required"),
    lastName: z.string().min(1, "Last name required"),
    isAdmin: z.boolean(),
  })).min(1, "At least one user is required"),
}));

export const AutoCheckpointGeoFenceSchema = lazyZodSchema((z) => z.object({
  identifier: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  radius: z.number(),
  notifyOnEntry: z.boolean().optional(),
  notifyOnExit: z.boolean().optional(),
  extras: z.record(z.string(), z.any()).optional(),
}));

export const TripLocationSnapshotSchema = lazyZodSchema((z) => z.object({
  timestamp: z.union([z.number(), z.string()]),
  lon: z.number().optional(),
  lat: z.number().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  accuracy: z.number().optional().nullable(),
  speed: z.number().optional().nullable(),
  altitude: z.number().optional().nullable(),
  heading: z.number().optional().nullable(),
  isMoving: z.boolean().optional(),
  activity: z.string().optional().nullable(),
  coords: z.object({
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    altitude: z.number().optional().nullable(),
    heading: z.number().optional().nullable(),
    accuracy: z.number().optional().nullable(),
    altitudeAccuracy: z.number().optional().nullable(),
    speed: z.number().optional().nullable(),
  }).optional(),
}));

export const TripSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional().nullable(),
  companyId: z.string().optional().nullable(),
  status: z.enum(["PLANNED", "ALLOCATED", "PRE_INSPECTED", "IN_PROGRESS", "ARRIVED", "COMPLETED"]).default("PLANNED"),
  requiresPreInspection: z.boolean().optional().default(false),
  locationSnapshots: z.array(z.any()).optional().default([]),
  capturedTripPreInspection: z.array(z.string()).optional().default([]),
  capturedTripDepart: z.array(z.string()).optional().default([]),
  capturedTripCheckPoint: z.array(z.string()).optional().default([]),
  capturedTripDelay: z.array(z.string()).optional().default([]),
  capturedTripArrive: z.array(z.string()).optional().default([]),
  capturedTripComplete: z.array(z.string()).optional().default([]),
  capturedFuelTankIssue: z.array(z.string()).optional().default([]),
  capturedRefuel: z.array(z.string()).optional().default([]),
  isTrackingActive: z.boolean().optional().default(false),
  tripToken: z.string().optional().nullable(),
  pickupAddress: z.string().optional().nullable(),
  deliveryAddress: z.string().optional().nullable(),
  pickupDate: z.string().optional().nullable(),
  deliveryDate: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  creatorUid: z.string().optional().nullable(),
  creatorEmail: z.string().optional().nullable(),
  creatorId: z.string().optional().nullable(),
  externalReference: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  fleetId: z.string().optional().nullable(),
  fleetDescription: z.string().optional().nullable(),
  fleetDisplayName: z.string().optional().nullable(),
  fleetRegistration: z.string().optional().nullable(),
  fleetOdoOrHourBased: z.string().optional().nullable(),
  fleetUseFleetForRebateClaim: z.boolean().optional(),
  fleetGroupId: z.string().optional().nullable(),
  fleetGroupName: z.string().optional().nullable(),
  fleetOperatorId: z.string().optional().nullable(),
  fleetOperatorEmployeeCode: z.string().optional().nullable(),
  fleetOperatorIdentification: z.string().optional().nullable(),
  fleetOperatorNameAndSurname: z.string().optional().nullable(),
  autoCheckpoints: z.array(AutoCheckpointGeoFenceSchema).optional(),
  tripOwnerUid: z.string().optional().nullable(),
  pickupCoordinates: z.object({ latitude: z.number(), longitude: z.number() }).optional().nullable(),
  deliveryCoordinates: z.object({ latitude: z.number(), longitude: z.number() }).optional().nullable(),
  isOutOfSync: z.boolean().optional().default(false),
  notificationEmailAddresses: z.array(z.string()).optional(),
  notificationWhatsappNumbers: z.array(z.string()).optional(),
  emailNotificationThreadId: z.string().optional().nullable(),
  logoUrl: z.string().optional().nullable(),
  metadata: z.any().optional(),
}));

export const CapturedTemplateSchema = lazyZodSchema((z) => z.object({
  localResourceId: z.string().optional(),
  template: z.string().min(1, "Template type is required"),
  metadata: z.any().optional(),
  data: z.record(z.string(), z.any()).default({}),
}));

export const TemplateSubmissionFieldSchema = lazyZodSchema((z) => z.object({
  type: z.string().optional(),
  value: z.any(),
  label: z.string().optional(),
  metadata: z.any().optional(),
}));

export const FlexFormSubmissionSchema = lazyZodSchema((z) => z.object({
  localResourceId: z.string().optional(),
  templateId: z.string().min(1, "Template ID is required"),
  templateName: z.string().optional(),
  companyId: z.string().optional().nullable(),
  submitterName: z.string().optional(),
  submitterUid: z.string().optional(),
  submitterEmail: z.string().optional(),
  deviceCaptured: z.union([z.string(), z.any()]).optional(),
  data: z.record(z.string(), z.any()).optional().default({}),
  captureFlow: z.enum(["guest", "authenticated"]).optional(),
  createdBy: z.string().optional(),
  gpsLocation: z.any().optional(),
  metadata: z.any().optional(),
}));

export const FlexAIStoryTemplateSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional(),
  templateId: z.string().min(1, "Template ID is required"),
  companyId: z.string().min(1, "Company ID is required"),
  handlebarsTemplate: z.string().min(1, "Handlebars template is required"),
  active: z.boolean().optional(),
  metadata: z.any().optional(),
}));
